/**
 * Constants used by the DrillFormPanel — controlled vocabularies for
 * every chip group. Kept in one file so the form, the validator, and
 * the completeness badge can all read from the same source.
 */

import { CORNERS } from '@4cm/shared/corners';

export { CORNERS };

export const DRILL_TYPES = [
  { id: 'WARM_UP', label: 'Warm-up' },
  { id: 'TECHNICAL', label: 'Technical' },
  { id: 'TACTICAL', label: 'Tactical' },
  { id: 'SMALL_SIDED_GAME', label: 'Small-sided game' },
  { id: 'COOL_DOWN', label: 'Cool-down' },
] as const;

export type DrillTypeId = typeof DRILL_TYPES[number]['id'];

export const DIFFICULTIES = [
  { id: 'FOUNDATION', label: 'Foundation' },
  { id: 'INTERMEDIATE', label: 'Intermediate' },
  { id: 'ADVANCED', label: 'Advanced' },
] as const;

export type DifficultyId = typeof DIFFICULTIES[number]['id'];

export const PHASES_OF_PLAY = [
  { id: 'attacking', label: 'Attacking', glyph: '↑' },
  { id: 'defending', label: 'Defending', glyph: '↓' },
  { id: 'transitions', label: 'Transitions', glyph: '↔' },
  { id: 'individual', label: 'Individual', glyph: '●' },
] as const;

export type PhaseOfPlayId = typeof PHASES_OF_PLAY[number]['id'];

export const CURRICULUM_PHASES = [
  'Foundation I', 'Foundation II', 'Foundation III',
  'Progression I', 'Progression II', 'Progression III',
  'Performance',
] as const;

export const EQUIPMENT_OPTIONS = [
  'cones', 'balls', 'bibs', 'goals', 'poles', 'mannequins',
  'agility ladder', 'hurdles', 'ropes', 'nets', 'rebounder',
  'corner flags', 'training discs',
] as const;

// Sub-corners — same set as packages/db/seed/subCorners.ts, mirrored
// here so the form doesn't pull from the seed module (which isn't a
// frontend export).
export const SUB_CORNERS: { cornerId: string; label: string }[] = [
  // Technical / Tactical
  { cornerId: 'corner-technical', label: 'First Touch' },
  { cornerId: 'corner-technical', label: 'Passing' },
  { cornerId: 'corner-technical', label: 'Finishing' },
  { cornerId: 'corner-technical', label: 'Dribbling' },
  { cornerId: 'corner-technical', label: 'Decision-Making' },
  { cornerId: 'corner-technical', label: '360° Awareness' },
  { cornerId: 'corner-technical', label: 'Pressing' },
  // Physical
  { cornerId: 'corner-physical', label: 'Speed' },
  { cornerId: 'corner-physical', label: 'Agility' },
  { cornerId: 'corner-physical', label: 'Strength' },
  { cornerId: 'corner-physical', label: 'Endurance' },
  { cornerId: 'corner-physical', label: 'Reaction Time' },
  // Psychological
  { cornerId: 'corner-psych', label: 'Confidence' },
  { cornerId: 'corner-psych', label: 'Composure' },
  { cornerId: 'corner-psych', label: 'Resilience' },
  { cornerId: 'corner-psych', label: 'Coachability' },
  // Social
  { cornerId: 'corner-social', label: 'Communication' },
  { cornerId: 'corner-social', label: 'Teamwork' },
  { cornerId: 'corner-social', label: 'Leadership' },
];

// ---- KB overlay vocabularies (migration 00060) -----------------------

export const DEVELOPMENTAL_BANDS = [
  { id: 'band_4_6', label: '4–6' },
  { id: 'band_7_11', label: '7–11' },
  { id: 'band_12_14', label: '12–14' },
  { id: 'band_15_16', label: '15–16' },
  { id: 'band_17_21', label: '17–21' },
  { id: 'open_age', label: 'Open age' },
] as const;

export const MATURITY_CONTEXTS = [
  { id: 'pre_phv', label: 'Pre-PHV' },
  { id: 'circa_phv', label: 'Circa-PHV' },
  { id: 'post_phv', label: 'Post-PHV' },
  { id: 'na', label: 'N/A' },
] as const;

export const CROSS_CUTTING_OVERLAYS = [
  { id: 'maturation', label: 'Maturation' },
  { id: 'safeguarding', label: 'Safeguarding' },
  { id: 'dual_career', label: 'Dual career' },
  { id: 'inclusion', label: 'Inclusion' },
  { id: 'transition_release', label: 'Transition / release' },
  { id: 'monitoring_tech', label: 'Monitoring tech' },
] as const;

export const EVIDENCE_TYPES = [
  { id: 'official_guidance', label: 'Official guidance' },
  { id: 'licensing', label: 'Licensing' },
  { id: 'technical_report', label: 'Technical report' },
  { id: 'systematic_review', label: 'Systematic review' },
  { id: 'original_study', label: 'Original study' },
  { id: 'expert_consensus', label: 'Expert consensus' },
  { id: 'case_study', label: 'Case study' },
] as const;

export const EVIDENCE_STRENGTHS = [
  { id: 'high', label: 'High' },
  { id: 'moderate', label: 'Moderate' },
  { id: 'emerging', label: 'Emerging' },
  { id: 'practice_informed', label: 'Practice-informed' },
] as const;

export const TARGET_ROLES = [
  { id: 'head_coach', label: 'Head coach' },
  { id: 'assistant', label: 'Assistant' },
  { id: 'analyst', label: 'Analyst' },
  { id: 'sc', label: 'S&C' },
  { id: 'welfare_lead', label: 'Welfare lead' },
  { id: 'parent', label: 'Parent' },
  { id: 'player', label: 'Player' },
  { id: 'club_admin', label: 'Club admin' },
] as const;

// ---- Form draft + completeness ---------------------------------------

export interface DrillFormDraft {
  name: string;
  description: string;
  duration_mins: number;
  drill_type: DrillTypeId | null;
  difficulty: DifficultyId;
  primary_phase: PhaseOfPlayId | null;
  sub_themes: string[];
  curriculum_phase: string[];
  corner_ids: string[];
  primary_corner_id: string | null;
  sub_corner_ids: string[];
  min_players: number | null;
  max_players: number | null;
  space_required: string;
  equipment: string[];
  phase_objective: string;
  four_cm_connection: string;
  coaching_points: string[];
  progressions: string[];
  regressions: string[];
  // KB overlays
  developmental_band: string[];
  maturity_context: string[];
  cross_cutting_overlays: string[];
  evidence_type: string | null;
  evidence_strength: string | null;
  target_roles: string[];
}

/** Returns N/8 completeness score and per-criterion breakdown. */
export function completenessOf(d: DrillFormDraft): { score: number; max: 8; checks: { id: string; label: string; ok: boolean }[] } {
  const checks = [
    { id: 'name', label: 'Name', ok: d.name.trim().length > 0 },
    { id: 'description', label: 'Description (≥10 chars)', ok: d.description.trim().length >= 10 },
    { id: 'corner', label: 'At least one 4-corner tag', ok: d.corner_ids.length > 0 },
    { id: 'primary_corner', label: 'Primary corner', ok: !!d.primary_corner_id },
    { id: 'phase', label: 'Primary phase of play', ok: !!d.primary_phase },
    { id: 'space', label: 'Space required', ok: d.space_required.trim().length > 0 },
    { id: 'coaching', label: 'At least one coaching point', ok: d.coaching_points.some((s) => s.trim().length > 0) },
    { id: 'objective', label: 'Phase objective', ok: d.phase_objective.trim().length > 0 },
  ];
  const score = checks.filter((c) => c.ok).length;
  return { score, max: 8, checks };
}
